import { useEffect, useState } from "react";

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { HttpMethodProps } from "../types";
import { DefaultErrorNotification, showValidationErrors } from "../methods";
import { UploadFilesResponse } from "../responses";
import { Endpoints } from "../endpoints";

interface RequestOptions {
  method: HttpMethodProps;
  route: string;
  body?: any;
}
export function usePerformRequest<Type>({
  method,
  route,
  body,
}: RequestOptions) {
  const [data, setData] = useState<Type>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [error, setError] = useState<AxiosError>();
  const [status, setStatus] = useState<number | string>("undefined");
  const [isLoading, setLoading] = useState<boolean>(false);
  const reloadData = async () => {
    const token = localStorage.getItem("t");
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `${token}`,
      },
      method,
      data: body,
      url: route,
    };
    setLoading(true);
    const r: any = await axios.request(config).catch((e: AxiosError) => {
      setError(e);
      setStatus(e.response?.status as number);

      if (e?.status === 403) {
        // @ts-ignore
        if (e?.response && e.response?.data && e.response.data?.data[0]) {
          // @ts-ignore
          DefaultErrorNotification(e.response.data?.data[0]);
        } else {
          // @ts-ignore
          DefaultErrorNotification(e.response?.data?.message);
        }
      }
      return {
        status: e.response?.status,
      };
    });
    setResponse(r as AxiosResponse);
    setStatus(r.status);
    setLoading(false);
    setData(r?.data ?? undefined);
  };
  useEffect(() => {
    reloadData().catch(() => setLoading(false));
  }, [route]);

  return { data, response, isLoading, reloadData, status, error };
}

interface PerformRequestOptions {
  method: HttpMethodProps;
  data?: any;
  route: string;
  headers?: any;
  doNotUseAuthorization?: boolean;
  callback?: () => any;
}

export async function PerformRequest<Type>({
  method,
  data,
  route,
  headers,
  doNotUseAuthorization,
  callback,
}: PerformRequestOptions) {
  const token = localStorage.getItem("t");

  const requestHeaders = doNotUseAuthorization
    ? { ...headers }
    : { Authorization: `${token}`, ...headers };
  const config = {
    method,
    data,
    url: route,
    headers: requestHeaders,
  };

  const r: any = await axios.request(config).catch((e: AxiosError) => {
    if (callback) callback();
    return {
      status: e.response?.status,
      message: (e?.response?.data as any)?.message,
      data: (e?.response?.data as any)?.data,
    };
  });
  if (r.status === 403) {
    if (callback) callback();
    DefaultErrorNotification(r?.data ? r.data[0] : r.message);
  } else {
    return {
      status: r.status as number,
      data: r?.data as Type,
      message: r?.message ?? undefined,
    };
  }
}
export const UploadFiles = async (
  files: File[],
  large?: boolean,
  provider: "s3" | "r2" = "s3"
) => {
  let data = new FormData();
  files.map((f) => {
    data.append("files", f);
  });
  const uploadEndpoint = large
    ? provider === "r2"
      ? Endpoints.GetUploadLargeFileR2()
      : Endpoints.GetUploadLargeFile()
    : provider === "r2"
    ? Endpoints.GetUploadFileR2()
    : Endpoints.GetUploadFile();
  const config: AxiosRequestConfig = {
    method: uploadEndpoint.method,
    maxBodyLength: Infinity,
    url: uploadEndpoint.route,
    data,
  };
  const r = await axios.request(config);
  const { data: response, status } = r;
  return {
    status,
    data: response.data,
    message: response.message,
  } as UploadFilesResponse;
};
